import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { navLinks, site } from '@/utils/site';

interface SiteHeaderProps {
  transparentTop?: boolean;
}

export default function SiteHeader({ transparentTop = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = !transparentTop || scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="黑料看点 首页">
          <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-fire-fill text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg md:text-xl tracking-wide ${
                solid ? 'text-foreground-950' : 'text-background-50'
              }`}
            >
              {site.name}
            </span>
            <span
              className={`text-[10px] tracking-[0.25em] ${
                solid ? 'text-foreground-500' : 'text-background-50/70'
              }`}
            >
              KANDIAN
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5" aria-label="主导航">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `px-2.5 py-2 rounded-md text-[13px] whitespace-nowrap transition-colors ${
                  isActive
                    ? solid
                      ? 'text-primary-700 bg-primary-50 font-semibold'
                      : 'text-background-50 font-semibold bg-background-50/15'
                    : solid
                      ? 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
                      : 'text-background-50/85 hover:text-background-50 hover:bg-background-50/15'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <Link
            to="/contact"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              solid
                ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                : 'bg-background-50 text-foreground-950 hover:bg-background-100'
            }`}
          >
            <i className="ri-mail-send-line" />
            推荐看点
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={open ? '关闭菜单' : '打开菜单'}
          aria-expanded={open}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
            solid ? 'text-foreground-900 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/15'
          }`}
        >
          <i className={`text-2xl ${open ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-background-50 border-t border-background-200">
          <nav className="px-4 py-3 flex flex-col" aria-label="移动端导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm transition-colors ${
                    isActive
                      ? 'text-primary-700 bg-primary-50 font-semibold'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-mail-send-line" />
              推荐看点
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}